import React, { useState } from 'react';
import { connect } from 'react-redux';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { getSelectedOrganizationProfile } from '../../util/organizations';
import { useReactToChange } from '../../util/hooks';
import { useOrgRole } from '../../hooks/useOrgRole';
import { DASHBOARD_PAGES } from '../../routeConfiguration';
import { selectCreatorUnreadCount } from '../../ducks/sendbird.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import {
  NamedLink,
  NamedRedirect,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  MenuAction,
  Counter,
  IconButton,
  Logo,
  DashboardPhoneTopBar,
  DashboardTabBar,
  Icon,
} from '..';
import DashboardSwitcher from '../DashboardWrapper/DashboardSwitcher';
import PageFamily from '../Page/PageFamily';

import css from './DashboardLayout.module.css';

// localStorage key for tracking last visited context
const DASHBOARD_CONTEXT_KEY = 'dashboardLastContext';
const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';

const isSettingsPage = (currentPage, prefix) =>
  typeof currentPage === 'string' && currentPage.startsWith(prefix);

// Sidebar items for a creator, from the Dashboard board.
const creatorNav = ({ currentPage, creatorUnreadCount }) => [
  { id: 'CreatorHomePage', icon: 'home', labelId: 'DashboardWrapper.homeTabTitle' },
  {
    id: 'CreatorManageListingsPage',
    icon: 'list',
    labelId: 'DashboardWrapper.listingsTabTitle',
  },
  { id: 'CreatorCalendarPage', icon: 'calendar', labelId: 'DashboardWrapper.calendarTabTitle' },
  {
    id: 'CreatorConversationsPage',
    icon: 'message',
    labelId: 'DashboardWrapper.conversationsTabTitle',
    count: creatorUnreadCount,
  },
  { id: 'CreatorAudiencePage', icon: 'users', labelId: 'DashboardWrapper.audienceTabTitle' },
  { id: 'CreatorInsightsPage', icon: 'insights', labelId: 'DashboardWrapper.insightsTabTitle' },
  {
    id: 'CreatorSettingsProfilePage',
    icon: 'settings',
    labelId: 'DashboardWrapper.settingsTabTitle',
    selected:
      currentPage === 'EditCreatorProfilePage' || isSettingsPage(currentPage, 'CreatorSettings'),
  },
];

// Sidebar items for an organization. The server is the authoritative gate; `can` only decides
// which entries to show so people don't see tabs that would 403 or show empty. Owners land on
// Settings > Profile (branding), everyone else on Settings > Teams (the sub-page they can use).
const orgNav = ({ currentPage, orgId, orgUnreadCount, can, isOwner }) => {
  const orgTabsConfig = [
    {
      requiredCan: 'viewDashboard',
      id: 'OrganizationHomePage',
      icon: 'home',
      labelId: 'DashboardWrapper.homeTabTitle',
    },
    {
      requiredCan: 'viewDashboard',
      id: 'OrgManageShowsPage',
      icon: 'list',
      labelId: 'DashboardWrapper.pagesTabTitle',
    },
    {
      requiredCan: 'viewDashboard',
      id: 'OrgCalendarPage',
      icon: 'calendar',
      labelId: 'DashboardWrapper.calendarTabTitle',
    },
    {
      requiredCan: 'viewDashboard',
      id: 'OrgConversationsPage',
      icon: 'message',
      labelId: 'DashboardWrapper.conversationsTabTitle',
      count: orgUnreadCount,
    },
    {
      requiredCan: 'viewRentedAudience',
      id: 'OrgAudiencePage',
      icon: 'users',
      labelId: 'DashboardWrapper.audienceTabTitle',
    },
    {
      requiredCan: 'viewInsights',
      id: 'OrgInsightsPage',
      icon: 'insights',
      labelId: 'DashboardWrapper.insightsTabTitle',
    },
    {
      requiredCan: 'viewDashboard',
      id: isOwner ? 'OrgSettingsProfilePage' : 'OrgSettingsTeamsPage',
      icon: 'settings',
      labelId: 'DashboardWrapper.settingsTabTitle',
      selected: isSettingsPage(currentPage, 'OrgSettings'),
    },
  ];
  return orgTabsConfig
    .filter(t => !t.requiredCan || can[t.requiredCan])
    .map(({ requiredCan, ...t }) => ({ ...t, params: { orgId } }));
};

// The "Create ▾" menu, from the DashCreateMenu board. Only existing routes; plan and upgrade
// entries stay out (Phase 1, plan 6p). The org's "New event" waits for a calendar deep link.
const createMenuItems = ({ isOrganizationContext, orgId, can }) =>
  isOrganizationContext
    ? [
        {
          key: 'production',
          icon: 'list',
          labelId: 'DashboardWrapper.createPage',
          descriptionId: 'DashboardLayout.createProductionDescription',
          linkProps: { name: 'NewListingPage', to: { search: `?orgId=${orgId}` } },
          show: can.createShows,
        },
        {
          key: 'list',
          icon: 'person',
          labelId: 'DashboardWrapper.createList',
          descriptionId: 'DashboardLayout.createListDescription',
          linkProps: {
            name: 'OrgAudiencePage',
            params: { orgId, tab: 'lists' },
            to: { search: '?mode=create' },
          },
          show: can.viewRentedAudience,
        },
        {
          key: 'email',
          icon: 'mail',
          labelId: 'DashboardLayout.createEmail',
          descriptionId: 'DashboardLayout.createEmailDescription',
          linkProps: {
            name: 'OrgAudiencePage',
            params: { orgId, tab: 'emails' },
            to: { search: '?mode=create' },
          },
          show: can.sendNewsletters,
        },
      ].filter(item => item.show)
    : [
        {
          key: 'liveRoom',
          icon: 'list',
          labelId: 'DashboardWrapper.createLiveRoom',
          descriptionId: 'DashboardLayout.createLiveRoomDescription',
          linkProps: { name: 'NewLiveRoomPage' },
        },
        {
          key: 'episode',
          icon: 'calendar',
          labelId: 'DashboardWrapper.createEpisode',
          descriptionId: 'DashboardLayout.createEpisodeDescription',
          linkProps: { name: 'CreatorManageListingsPage', to: { search: '?newEpisode=1' } },
        },
        {
          key: 'list',
          icon: 'person',
          labelId: 'DashboardWrapper.createList',
          descriptionId: 'DashboardLayout.createListDescription',
          linkProps: {
            name: 'CreatorAudiencePage',
            params: { tab: 'lists' },
            to: { search: '?mode=create' },
          },
        },
        {
          key: 'email',
          icon: 'mail',
          labelId: 'DashboardLayout.createEmail',
          descriptionId: 'DashboardLayout.createEmailDescription',
          linkProps: {
            name: 'CreatorAudiencePage',
            params: { tab: 'emails' },
            to: { search: '?mode=create' },
          },
        },
      ];

const PAGE_TITLE_IDS = {
  CreatorHomePage: 'DashboardWrapper.homePageTitle',
  CreatorManageListingsPage: 'DashboardWrapper.listingsPageTitle',
  CreatorConversationsPage: 'DashboardWrapper.conversationsPageTitle',
  CreatorAudiencePage: 'DashboardWrapper.audiencePageTitle',
  CreatorInsightsPage: 'DashboardWrapper.analyticsPageTitle',
  EditCreatorProfilePage: 'DashboardWrapper.settingsPageTitle',
  OrganizationHomePage: 'DashboardWrapper.homePageTitle',
  OrgManageShowsPage: 'DashboardWrapper.pagesPageTitle',
  OrgConversationsPage: 'DashboardWrapper.conversationsPageTitle',
  OrgAudiencePage: 'DashboardWrapper.audiencePageTitle',
  MembersPage: 'DashboardWrapper.teamPageTitle',
  OrgInsightsPage: 'DashboardWrapper.analyticsPageTitle',
  OrganizationSettingsPage: 'DashboardWrapper.settingsPageTitle',
};

// The phone tab bar's four tabs, by sidebar icon; every other page goes under More
const TAB_KEY_BY_ICON = {
  home: 'home',
  calendar: 'calendar',
  message: 'inbox',
  users: 'audience',
};

// The phone tab bar's items: the sidebar's pages, then the legal pages (under More, as the
// sidebar keeps them reachable on desktop)
const tabBarItems = (navItems, intl) => [
  // The four tabs take the tab bar's own labels and icons (Inbox, not Conversations)
  ...navItems.map(item =>
    TAB_KEY_BY_ICON[item.icon]
      ? { key: TAB_KEY_BY_ICON[item.icon], linkProps: { name: item.id, params: item.params } }
      : {
          key: item.id,
          label: intl.formatMessage({ id: item.labelId }),
          icon: <Icon name={item.icon} />,
          linkProps: { name: item.id, params: item.params },
        }
  ),
  {
    key: 'privacy',
    label: intl.formatMessage({ id: 'Footer.linkToPrivacyPolicyPage' }),
    icon: <Icon name="document" />,
    linkProps: { name: 'PrivacyPolicyPage' },
  },
  {
    key: 'terms',
    label: intl.formatMessage({ id: 'Footer.linkToTermsOfServicePage' }),
    icon: <Icon name="document" />,
    linkProps: { name: 'TermsOfServicePage' },
  },
];

const NavItem = props => {
  const { item, intl } = props;
  const label = intl.formatMessage({ id: item.labelId });
  return (
    <li>
      <NamedLink
        name={item.id}
        params={item.params}
        className={classNames(css.navItem, { [css.navItemActive]: item.selected })}
        aria-current={item.selected ? 'page' : undefined}
      >
        <Icon name={item.icon} className={css.navIcon} />
        <span className={css.navLabel}>{label}</span>
        {item.count > 0 ? <Counter count={item.count} /> : null}
      </NamedLink>
    </li>
  );
};

/**
 * The dashboard shell: the 248px sidebar (logo, navigation per role, a footer slot and the
 * switcher), the 72px header (back, title, messages, "Create ▾") and the content on the dashboard
 * surface. Props in, markup out: `DashboardLayout` below connects it to the store.
 *
 * On phones the sidebar and header give way to the phone top bar (the switcher, or ‹ and the
 * title on a sub-page) and the tab bar (Home, Calendar, Inbox, Audience, More with the other
 * pages and the legal pages). Slots: `notices` (above the content), `sidebarFooter` (above the
 * switcher), `phoneTopBar` and `phoneTabBar` (a page's own bars, in place of those).
 */
export const DashboardLayoutComponent = props => {
  const {
    rootClassName,
    className,
    children,
    currentPage,
    currentUser,
    organizationProfiles,
    orgId,
    isOrganizationContext,
    can,
    isOwner,
    creatorUnreadCount,
    orgUnreadCount,
    pageTitle,
    breadcrumb,
    narrowContent,
    contentPadding,
    notices,
    sidebarFooter,
    phoneTopBar: phoneTopBarProp,
    phoneTabBar: phoneTabBarProp,
    onManageDisableScrolling,
  } = props;
  const intl = useIntl();
  const [isCreateOpen, setCreateOpen] = useState(false);

  const navItems = (isOrganizationContext
    ? orgNav({ currentPage, orgId, orgUnreadCount, can, isOwner })
    : creatorNav({ currentPage, creatorUnreadCount })
  ).map(item => ({ ...item, selected: item.selected ?? item.id === currentPage }));

  const createItems = createMenuItems({ isOrganizationContext, orgId, can });
  const unreadCount = isOrganizationContext ? orgUnreadCount : creatorUnreadCount;

  const selectedItem = navItems.find(item => item.selected);
  const titleId =
    PAGE_TITLE_IDS[currentPage] || selectedItem?.labelId || 'DashboardWrapper.homePageTitle';
  const title = pageTitle || intl.formatMessage({ id: titleId });

  const classes = classNames(rootClassName || css.root, className);

  const switcherProps = {
    currentUser,
    organizationProfiles,
    currentOrgId: orgId,
    currentPage,
  };

  // Phones (PhoneDash boards): the switcher, or ‹ and the title on a sub-page, at the top; the
  // four tabs and More at the bottom. A page can pass its own bars instead.
  const phoneTopBar =
    phoneTopBarProp ||
    (breadcrumb ? (
      <DashboardPhoneTopBar title={title} onBack={breadcrumb.onClick} />
    ) : (
      <DashboardPhoneTopBar switcher={<DashboardSwitcher {...switcherProps} layout="bar" />} />
    ));
  const phoneTabBar =
    phoneTabBarProp ||
    (onManageDisableScrolling ? (
      <DashboardTabBar
        items={tabBarItems(navItems, intl)}
        activeKey={selectedItem ? TAB_KEY_BY_ICON[selectedItem.icon] || selectedItem.id : null}
        unread={unreadCount}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    ) : null);

  return (
    <div className={classes} data-family="dashboard">
      <PageFamily family="dashboard" />
      <aside className={classNames(css.sidebar, { [css.hasPhoneTabBar]: !!phoneTabBar })}>
        <NamedLink
          className={css.logoLink}
          name={isBetaMode ? 'CommunityPage' : 'LandingPage'}
          title={intl.formatMessage({ id: 'Topbar.logoIcon' })}
        >
          <Logo className={css.logo} />
        </NamedLink>
        <nav className={css.nav}>
          <ul className={css.navList}>
            {navItems.map(item => (
              <NavItem key={item.id} item={item} intl={intl} />
            ))}
          </ul>
        </nav>
        <div className={css.sidebarFooter}>
          {sidebarFooter}
          <DashboardSwitcher {...switcherProps} />
          {/* The legal pages stay reachable from the dashboard */}
          <p className={css.legal}>
            <NamedLink className={css.legalLink} name="PrivacyPolicyPage">
              <FormattedMessage id="Footer.linkToPrivacyPolicyPage" />
            </NamedLink>
            <span aria-hidden="true">·</span>
            <NamedLink className={css.legalLink} name="TermsOfServicePage">
              <FormattedMessage id="Footer.linkToTermsOfServicePage" />
            </NamedLink>
          </p>
        </div>
      </aside>

      <div className={css.main}>
        {phoneTopBar ? <div className={css.phoneTopBar}>{phoneTopBar}</div> : null}
        <header className={classNames(css.header, { [css.hasPhoneTopBar]: !!phoneTopBar })}>
          {breadcrumb ? (
            <IconButton
              label={intl.formatMessage(
                { id: 'DashboardLayout.backTo' },
                { page: breadcrumb.label }
              )}
              onClick={breadcrumb.onClick}
            >
              <Icon name="chevronLeft" size={16} />
            </IconButton>
          ) : null}
          <h1 className={css.title}>{title}</h1>
          <NamedLink
            className={css.messages}
            name={isOrganizationContext ? 'OrgConversationsPage' : 'CreatorConversationsPage'}
            params={isOrganizationContext ? { orgId } : {}}
          >
            <Icon name="message" />
            <span className={css.srOnly}>
              <FormattedMessage id="DashboardWrapper.conversationsTabTitle" />
            </span>
            {unreadCount > 0 ? <Counter className={css.messagesCount} count={unreadCount} /> : null}
          </NamedLink>
          {createItems.length > 0 ? (
            <Menu
              className={css.createMenuRoot}
              contentPosition="left"
              useArrow={false}
              isOpen={isCreateOpen}
              onToggleActive={setCreateOpen}
            >
              <MenuLabel
                rootClassName={css.createButton}
                ariaLabel={intl.formatMessage({ id: 'DashboardWrapper.createButton' })}
              >
                <Icon name="add" size={14} />
                <span className={css.createLabel}>
                  <FormattedMessage id="DashboardWrapper.createButton" />
                </span>
                <Icon name="chevronDown" size={12} className={css.createChevron} />
              </MenuLabel>
              <MenuContent className={css.createMenu}>
                {createItems.map(item => (
                  <MenuItem key={item.key}>
                    <MenuAction
                      linkProps={item.linkProps}
                      onClick={() => setCreateOpen(false)}
                      icon={
                        <span className={css.createIcon}>
                          <Icon name={item.icon} size={16} />
                        </span>
                      }
                    >
                      <span className={css.createText}>
                        <span className={css.createTitle}>
                          <FormattedMessage id={item.labelId} />
                        </span>
                        <span className={css.createDescription}>
                          <FormattedMessage id={item.descriptionId} />
                        </span>
                      </span>
                    </MenuAction>
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          ) : null}
        </header>

        <main className={css.content}>
          {notices ? <div className={css.notices}>{notices}</div> : null}
          <div
            className={classNames(css.contentInner, {
              [css.narrow]: narrowContent,
              [css.padded]: contentPadding,
            })}
          >
            {children}
          </div>
        </main>
        {phoneTabBar ? <div className={css.phoneTabBar}>{phoneTabBar}</div> : null}
      </div>
    </div>
  );
};

DashboardLayoutComponent.defaultProps = {
  rootClassName: null,
  className: null,
  children: null,
  currentPage: null,
  currentUser: null,
  organizationProfiles: [],
  orgId: null,
  isOrganizationContext: false,
  can: {},
  isOwner: false,
  creatorUnreadCount: 0,
  orgUnreadCount: 0,
  pageTitle: null,
  breadcrumb: null,
  narrowContent: false,
  contentPadding: false,
  notices: null,
  sidebarFooter: null,
  phoneTopBar: null,
  phoneTabBar: null,
  onManageDisableScrolling: null,
};

DashboardLayoutComponent.propTypes = {
  rootClassName: string,
  className: string,
  children: node,
  currentPage: string,
  currentUser: propTypes.currentUser,
  organizationProfiles: arrayOf(propTypes.organization),
  orgId: string,
  isOrganizationContext: bool,
  // `useOrgRole(orgId).can`: which org entries to show
  can: object,
  isOwner: bool,
  creatorUnreadCount: number,
  orgUnreadCount: number,
  // Replaces the title taken from the current page
  pageTitle: string,
  // A back button before the title (e.g. { label: 'Lists', onClick })
  breadcrumb: shape({ label: string.isRequired, onClick: func.isRequired }),
  // Content at most 800px wide
  narrowContent: bool,
  // Content padded (24px 28px on desktop)
  contentPadding: bool,
  // Slots
  notices: node,
  sidebarFooter: node,
  phoneTopBar: node,
  phoneTabBar: node,
  // For the tab bar's More sheet (a Modal)
  onManageDisableScrolling: func,
};

/**
 * The shell with its data: the current user, organizations, role and unread counts from the
 * store, the redirects for people without access, and the last-visited dashboard context.
 */
const DashboardLayoutContainer = props => {
  const {
    currentUserLoaded,
    currentUserHasListings,
    organizationProfiles,
    orgId,
    currentPage,
    orgUnreadCounts,
    ...rest
  } = props;

  // The organization comes from the URL's orgId, not from the user profile.
  const selectedOrganization = orgId
    ? getSelectedOrganizationProfile(organizationProfiles, orgId)
    : null;
  const isOrganizationContext = !!orgId && !!selectedOrganization;
  const { can, isOwner } = useOrgRole(orgId);

  // Track the last visited context in localStorage.
  useReactToChange(`${orgId || ''}|${selectedOrganization ? '1' : '0'}`, () => {
    if (orgId && selectedOrganization) {
      localStorage.setItem(DASHBOARD_CONTEXT_KEY, `org:${orgId}`);
    } else if (!orgId) {
      localStorage.setItem(DASHBOARD_CONTEXT_KEY, 'creator');
    }
  });

  // orgId given but the user has no access: back to the creator dashboard. Only once the
  // profiles have loaded.
  if (orgId && !selectedOrganization && organizationProfiles?.length > 0) {
    return <NamedRedirect name="CreatorHomePage" />;
  }
  if (currentUserLoaded && !currentUserHasListings && !DASHBOARD_PAGES.includes(currentPage)) {
    return <NamedRedirect name="LandingPage" />;
  }

  return (
    <DashboardLayoutComponent
      {...rest}
      currentPage={currentPage}
      organizationProfiles={organizationProfiles}
      orgId={orgId}
      isOrganizationContext={isOrganizationContext}
      can={can}
      isOwner={isOwner}
      orgUnreadCount={(orgId && orgUnreadCounts[orgId]) || 0}
    />
  );
};

DashboardLayoutContainer.defaultProps = {
  currentUserHasListings: false,
  organizationProfiles: [],
  orgId: null,
  currentPage: null,
  orgUnreadCounts: {},
};

DashboardLayoutContainer.propTypes = {
  currentUserLoaded: bool.isRequired,
  currentUserHasListings: bool,
  organizationProfiles: arrayOf(propTypes.organization),
  orgId: string,
  currentPage: string,
  orgUnreadCounts: object,
};

const mapStateToProps = state => {
  const { currentUser, currentUserHasListings } = state.user;
  const { organizationProfiles } = state.organizations;
  return {
    currentUser,
    currentUserLoaded: !!ensureCurrentUser(currentUser).id,
    currentUserHasListings,
    organizationProfiles,
    creatorUnreadCount: selectCreatorUnreadCount(state),
    orgUnreadCounts: state.sendbird?.orgUnreadCounts || {},
  };
};

const mapDispatchToProps = {
  onManageDisableScrolling: manageDisableScrolling,
};

const DashboardLayout = connect(mapStateToProps, mapDispatchToProps)(DashboardLayoutContainer);

// The old name, kept while the dashboard pages move to DashboardLayout.
export const DashboardWrapper = DashboardLayout;

export default DashboardLayout;
